import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  getExternalTools,
  addExternalTool,
  updateExternalTool,
  removeExternalTool,
} from '@/data/externalTools';
import { toast } from 'sonner';

/**
 * Fetch user's external tools
 */
export function useExternalTools() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['external-tools', user?.id],
    queryFn: () => getExternalTools(user!.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * Add external tool mutation
 */
export function useAddExternalTool() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      toolName: string;
      category?: string;
      rating?: number;
      status?: 'owned' | 'tried';
      notes?: string;
    }) => addExternalTool({ userId: user!.id, ...params }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-tools', user?.id] });
      toast.success('Tool added to your stack');
    },
    onError: (error: any) => {
      console.error('Failed to add external tool:', error);
      toast.error('Failed to add tool');
    },
  });
}

/**
 * Update external tool mutation
 */
export function useUpdateExternalTool() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ toolId, updates }: { toolId: string; updates: any }) =>
      updateExternalTool(toolId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-tools', user?.id] });
      toast.success('Tool updated');
    },
    onError: (error: any) => {
      console.error('Failed to update external tool:', error);
      toast.error('Failed to update tool');
    },
  });
}

/**
 * Remove external tool mutation
 */
export function useRemoveExternalTool() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (toolId: string) => removeExternalTool(toolId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-tools', user?.id] });
      toast.success('Tool removed from your stack');
    },
    onError: (error: any) => {
      console.error('Failed to remove external tool:', error);
      toast.error('Failed to remove tool');
    },
  });
}
